import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Trash2, ScanLine, PackageCheck, PackageX, Boxes, Eye, X, Download, Lightbulb, Check, Undo2 } from '@/components/icons';
import { SectionHeader } from '@/components/ui/SectionHeader';
import { KpiCard, KPI_TEXT_SHADOW } from '@/components/ui/KpiCard';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/form/Input';
import { Select } from '@/components/form/Select';
import { FilterBar, FilterField } from '@/components/ui/FilterBar';
import { SearchableSelect } from '@/components/form/SearchableSelect';
import { Table } from '@/components/ui/Table';
import { ItemThumb } from '@/components/ui/ItemThumb';
import { useToast } from '@/components/ui/useToast';
import { apiError } from '@/lib/apiError';
import { listParties } from '@/api/parties';
import { lookupReturnSerial, lookupOnlineReturnUnit, createSalesReturn, listReturnLocations, listCustomerRecentInvoices, getReturnInvoiceDetail, getReturnOnlineOrder, listSalesReturns, salesReturnsExportPath, type InvoiceExpectedItem, type ReturnOnlineOrder, type SalesReturnSummary } from '@/api/salesReturn';
import { useSearchParams } from 'react-router-dom';
import { downloadExcel } from '@/lib/downloadExcel';
import { formatDate } from '@/lib/format';

interface ReturnLine {
  serialNo: string;
  itemName: string;
  imageUrl?: string | null;
  barcode?: string | null;
  qty: number;
  condition: 'Good' | 'Damaged';
  /** Online parcel: pieces from this packed serial can come back, up to maxQty. */
  maxQty?: number;
}

type Tab = 'new' | 'history';

export function SalesReturnPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const [tab, setTab] = useState<Tab>('new');
  const [customerId, setCustomerId] = useState<number | ''>('');
  const [locationCode, setLocationCode] = useState('');
  const [remarks, setRemarks] = useState('');
  const [invoiceNo, setInvoiceNo] = useState('');
  const [input, setInput] = useState('');
  const [scanning, setScanning] = useState(false);
  const [lines, setLines] = useState<ReturnLine[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const [invoiceOpen, setInvoiceOpen] = useState(false);
  // Articles expected back for the resolved invoice (empty when the invoice
  // isn't a known SalesInvoice, e.g. a free-typed SIS store-lot number).
  const [expected, setExpected] = useState<{ invoiceNo: string; items: InvoiceExpectedItem[]; title?: string } | null>(null);
  // Operator's checklist of which expected articles they intend to return.
  const [selected, setSelected] = useState<Set<string>>(new Set());
  // Return quantity per selected expected article (key → qty), for returning
  // by quantity without scanning each serial.
  const [qtyByKey, setQtyByKey] = useState<Record<string, number>>({});
  // Good / Damaged per selected expected article (default Good).
  const [conditionByKey, setConditionByKey] = useState<Record<string, 'Good' | 'Damaged'>>({});
  // Online (Shopify) RTO: no ERP invoice, so the parcel is found by order no or AWB.
  // Shipping's "Receive RTO" opens this page with ?order=<AWB>.
  const [searchParams] = useSearchParams();
  const [orderQuery, setOrderQuery] = useState(() => searchParams.get('order') ?? '');
  const [online, setOnline] = useState<ReturnOnlineOrder | null>(null);
  const [onlineError, setOnlineError] = useState<string | null>(null);

  const customersQ = useQuery({
    queryKey: ['parties', 'customer', 'return'],
    queryFn: () => listParties({ type: 'customer', pageSize: 500 }),
    staleTime: 300_000,
  });
  const customerOptions = useMemo(() => {
    const opts = (customersQ.data?.data ?? []).map((c) => ({ value: c.id, label: c.name }));
    // Online order: show the buyer on the shared Shopify party.
    if (!online) return opts;
    return [{ value: online.partyId, label: `${online.customerName} (Online)` }, ...opts.filter((o) => o.value !== online.partyId)];
  }, [customersQ.data, online]);

  // Recent invoices for the picked customer — click a chip to fill Invoice No.
  const recentInvoicesQ = useQuery({
    queryKey: ['sales-return-invoices', customerId],
    queryFn: () => listCustomerRecentInvoices(Number(customerId)),
    enabled: customerId !== '',
    staleTime: 60_000,
  });

  // Invoice dropdown options — the customer's invoices, filtered by what's typed.
  const invoiceMatches = useMemo(() => {
    const all = recentInvoicesQ.data ?? [];
    const q = invoiceNo.trim().toLowerCase();
    return q ? all.filter((i) => i.invoiceNo.toLowerCase().includes(q)) : all;
  }, [recentInvoicesQ.data, invoiceNo]);

  // Resolve the entered/selected invoice → its customer (auto-fill when blank)
  // and the articles expected back. Debounced so free-typing doesn't spam.
  useEffect(() => {
    const inv = invoiceNo.trim();
    if (orderQuery.trim()) return; // an online order owns the expected list
    if (!inv) { setExpected(null); return; }
    if (expected?.invoiceNo === inv) return; // already resolved
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const d = await getReturnInvoiceDetail(inv);
        if (cancelled) return;
        if (d) {
          setExpected({ invoiceNo: d.invoiceNo, items: d.items });
          setCustomerId((cur) => (cur === '' ? d.partyId : cur)); // fetch customer from invoice
        } else {
          setExpected(null);
        }
      } catch { if (!cancelled) setExpected(null); }
    }, 400);
    return () => { cancelled = true; clearTimeout(t); };
  }, [invoiceNo, expected?.invoiceNo, orderQuery]);

  // Resolve the online order no / AWB → customer + dispatched lines (debounced).
  useEffect(() => {
    const q = orderQuery.trim();
    setOnlineError(null);
    if (!q) {
      setOnline(null);
      setExpected((cur) => (cur?.invoiceNo.startsWith('order:') ? null : cur));
      return;
    }
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const d = await getReturnOnlineOrder(q);
        if (cancelled) return;
        setOnline(d);
        setLines([]); // a new parcel starts a fresh scan list
        setCustomerId(d.partyId);
        setExpected({
          invoiceNo: `order:${d.outwardGrnId}`,
          items: d.items,
          title: `Order ${d.orderName ?? d.grnNo}${d.awbNo ? ` · AWB ${d.awbNo}` : ''} · ${d.customerName}`,
        });
      } catch (err) {
        if (cancelled) return;
        setOnline(null);
        setExpected(null);
        setOnlineError(apiError(err, 'Order not found'));
      }
    }, 400);
    return () => { cancelled = true; clearTimeout(t); };
  }, [orderQuery]);

  // Scanned qty per item name — cross-checks progress against the expected list.
  const scannedByName = useMemo(() => {
    const m = new Map<string, number>();
    for (const l of lines) m.set(l.itemName.toLowerCase(), (m.get(l.itemName.toLowerCase()) ?? 0) + l.qty);
    return m;
  }, [lines]);

  // Expected-list checklist selection (reset whenever the invoice changes).
  useEffect(() => { setSelected(new Set()); setQtyByKey({}); setConditionByKey({}); }, [expected?.invoiceNo]);
  const expectedKeys = useMemo(() => (expected?.items ?? []).map((it) => `${it.itemCode}|${it.itemName}`), [expected]);
  const allExpectedSelected = expectedKeys.length > 0 && expectedKeys.every((k) => selected.has(k));
  const toggleExpected = (k: string) =>
    setSelected((prev) => { const n = new Set(prev); if (n.has(k)) n.delete(k); else n.add(k); return n; });
  const toggleAllExpected = () => setSelected(allExpectedSelected ? new Set() : new Set(expectedKeys));
  const setQtyForKey = (k: string, raw: string) =>
    setQtyByKey((prev) => ({ ...prev, [k]: Math.max(0, Math.floor(Number(raw) || 0)) }));

  // Quantity-based return lines — selected expected articles (with a code) whose
  // qty > 0. Sent alongside any scanned serials on save.
  const qtyLines = useMemo(() => {
    // Online parcels are received by scanning each piece, so no quantity lines.
    if (!expected || online) return [] as { itemCode: string; itemName: string; qty: number; condition: 'Good' | 'Damaged' }[];
    return expected.items
      .filter((it) => it.itemCode && selected.has(`${it.itemCode}|${it.itemName}`))
      .map((it) => {
        const key = `${it.itemCode}|${it.itemName}`;
        return { itemCode: it.itemCode, itemName: it.itemName, qty: qtyByKey[key] ?? it.qty, condition: conditionByKey[key] ?? 'Good' };
      })
      .filter((l) => l.qty > 0);
  }, [expected, online, selected, qtyByKey, conditionByKey]);
  const qtyLinePieces = useMemo(() => qtyLines.reduce((a, l) => a + l.qty, 0), [qtyLines]);

  // Return history — every recorded return, for the tracking table.
  const returnsQ = useQuery({ queryKey: ['sales-returns'], queryFn: listSalesReturns, staleTime: 30_000 });
  const returns = returnsQ.data ?? [];
  const [detail, setDetail] = useState<SalesReturnSummary | null>(null);
  const [sortBy, setSortBy] = useState<'date' | 'customer' | 'invoice'>('date');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const sortedReturns = useMemo(() => {
    const d = sortDir === 'asc' ? 1 : -1;
    return [...returns].sort((a, b) => {
      let c = 0;
      if (sortBy === 'customer') c = (a.partyName ?? '').localeCompare(b.partyName ?? '');
      else if (sortBy === 'invoice') c = (a.invoiceNo ?? '').localeCompare(b.invoiceNo ?? '');
      else c = new Date(a.returnDate).getTime() - new Date(b.returnDate).getTime();
      return c * d || (a.id - b.id) * d;
    });
  }, [returns, sortBy, sortDir]);

  const locationsQ = useQuery({
    queryKey: ['sales-return', 'locations'],
    queryFn: listReturnLocations,
    staleTime: 60_000,
  });
  const locations = locationsQ.data ?? [];
  const locationOptions = useMemo(
    () => locations.map((l) => ({ value: l.code, label: `${l.code} · ${l.name}`, sublabel: l.free != null ? `${l.free} free of ${l.capacity}` : `${l.used} placed` })),
    [locations],
  );

  // Focus the item scan box the moment a location is chosen (Step 1 → Step 2).
  useEffect(() => {
    if (locationCode) inputRef.current?.focus();
  }, [locationCode]);

  // Pieces count both scanned boxes and quantity lines ticked in "Expected to Return".
  const goodUnits = [...lines, ...qtyLines].filter((l) => l.condition === 'Good').reduce((a, l) => a + l.qty, 0);
  const dmgUnits = [...lines, ...qtyLines].filter((l) => l.condition === 'Damaged').reduce((a, l) => a + l.qty, 0);
  const totalUnits = goodUnits + dmgUnits;

  async function handleScan() {
    const serial = input.trim();
    if (!serial) return;
    if (!locationCode) {
      toast.push('Pick a location to place returns first', 'warning');
      return;
    }
    setInput('');
    // Online parcel: each scan is one piece back onto the serial it was packed from.
    if (online) {
      setScanning(true);
      try {
        const r = await lookupOnlineReturnUnit(online.outwardGrnId, serial);
        if (!r.returnable) { toast.push(r.reason ?? `"${serial}" can't be returned`, 'error'); return; }
        const taken = (sn: string) => lines.find((l) => l.serialNo.toUpperCase() === sn.toUpperCase())?.qty ?? 0;
        const unit = r.units.find((u) => taken(u.serialNo) < u.maxQty);
        if (!unit) {
          const all = r.units.reduce((a, u) => a + u.maxQty, 0);
          toast.push(`All ${all} piece${all === 1 ? '' : 's'} of "${r.units[0]?.itemName ?? serial}" from this order are already scanned`, 'warning');
          return;
        }
        setLines((prev) => {
          const i = prev.findIndex((l) => l.serialNo.toUpperCase() === unit.serialNo.toUpperCase());
          if (i >= 0) return prev.map((l, j) => (j === i ? { ...l, qty: l.qty + 1 } : l));
          return [{ serialNo: unit.serialNo, itemName: unit.itemName, imageUrl: unit.imageUrl, barcode: unit.barcode, qty: 1, maxQty: unit.maxQty, condition: 'Good' }, ...prev];
        });
      } catch (err) {
        toast.push(apiError(err, 'Not found in this parcel'), 'error');
      } finally {
        setScanning(false);
        inputRef.current?.focus();
      }
      return;
    }
    if (lines.some((l) => l.serialNo.toUpperCase() === serial.toUpperCase())) {
      toast.push(`"${serial}" already added`, 'warning');
      inputRef.current?.focus();
      return;
    }
    setScanning(true);
    try {
      const s = await lookupReturnSerial(serial);
      if (!s.returnable) {
        toast.push(s.reason ?? `"${serial}" can't be returned`, 'error');
        return;
      }
      setLines((prev) => [
        { serialNo: s.serialNo, itemName: s.itemName, imageUrl: s.imageUrl, barcode: s.barcode, qty: s.qty, condition: 'Good' },
        ...prev,
      ]);
    } catch (err) {
      toast.push(apiError(err, 'Serial not found'), 'error');
    } finally {
      setScanning(false);
      inputRef.current?.focus();
    }
  }

  const saveMut = useMutation({
    mutationFn: () =>
      createSalesReturn({
        partyId: customerId ? Number(customerId) : undefined,
        invoiceNo: invoiceNo.trim() || undefined,
        outwardGrnId: online?.outwardGrnId,
        remarks: remarks.trim() || undefined,
        locationCode: locationCode.trim() || undefined,
        items: [
          ...lines.map((l) => ({ serialNo: l.serialNo, condition: l.condition, ...(online ? { qty: l.qty } : {}) })),
          ...qtyLines.map((l) => ({ itemCode: l.itemCode, qty: l.qty, condition: l.condition })),
        ],
      }),
    onSuccess: (r) => {
      toast.push(`Return ${r.returnNo} saved — ${r.items} item(s), ${r.totalQty} pcs back in stock${r.keptAt?.length ? ` · ${r.keptAt.join('; ')}` : ''}${r.sisReturnsClosed ? ` · ${r.sisReturnsClosed} pc(s) closed pending SIS return(s)` : ''}`, 'success');
      setLines([]);
      setRemarks('');
      setInvoiceNo('');
      setCustomerId('');
      setLocationCode('');
      setExpected(null);
      setSelected(new Set());
      setQtyByKey({});
      setConditionByKey({});
      setOrderQuery('');
      setOnline(null);
      qc.invalidateQueries({ queryKey: ['wh-stock'] });
      qc.invalidateQueries({ queryKey: ['shipping-rows'] });
      qc.invalidateQueries({ queryKey: ['sales-returns'] });
      qc.invalidateQueries({ queryKey: ['sales-return', 'locations'] });
      inputRef.current?.focus();
    },
    onError: (e) => toast.push(apiError(e, 'Save failed'), 'error'),
  });

  const canScan = !!locationCode;

  return (
    <div className="flex flex-col gap-2 sm:gap-3 lg:h-full sm:[&_input:not([type=checkbox]):not([type=number])]:h-8 sm:[&_input:not([type=checkbox]):not([type=number])]:py-1 sm:[&_input:not([type=checkbox]):not([type=number])]:text-xs">
      <SectionHeader
        title="Sales Return"      />

      {/* Tab bar */}
      <div className="mb-1 inline-flex shrink-0 gap-1 self-start rounded-lg border border-[var(--border-color)] bg-[var(--card-bg)] p-1">
        <TabButton active={tab === 'new'} onClick={() => setTab('new')} label="New Return" />
        <TabButton active={tab === 'history'} onClick={() => setTab('history')} label={`Return History${returns.length ? ` (${returns.length})` : ''}`} />
      </div>

      {tab === 'new' && (
      <>
      {/* KPI strip — phone: one compact row of four; desktop: the standard tiles */}
      <div className="grid shrink-0 grid-cols-4 gap-1.5 sm:hidden">
        {([
          ['Scanned', lines.length, '#3b82f6'],
          ['Pieces', totalUnits, '#f59e0b'],
          ['Good', goodUnits, '#10b981'],
          ['Damaged', dmgUnits, '#ef4444'],
        ] as const).map(([label, value, accent]) => (
          <div key={label} className="rounded-lg px-2 py-1.5 text-white shadow-sm" style={{ background: accent, textShadow: KPI_TEXT_SHADOW }}>
            <div className="truncate text-[9px] font-extrabold uppercase tracking-wide text-white/90">{label}</div>
            <div className="text-lg font-extrabold leading-tight">{value}</div>
          </div>
        ))}
      </div>
      <div className="hidden shrink-0 grid-cols-2 gap-2 sm:grid sm:grid-cols-4 sm:gap-3">
        <KpiCard label="Items Scanned" value={lines.length} accent="#3b82f6" Icon={ScanLine} />
        <KpiCard label="Total Pieces" value={totalUnits} accent="#f59e0b" Icon={Boxes} />
        <KpiCard label="Good (to stock)" value={goodUnits} accent="#10b981" Icon={PackageCheck} />
        <KpiCard label="Damaged (quarantine)" value={dmgUnits} accent="#ef4444" Icon={PackageX} />
      </div>

      {/* Return details — optional */}
      <div className="shrink-0 rounded-card border border-[var(--border-color)] bg-[var(--card-bg)] backdrop-blur-xl">
        <div className="flex min-h-[44px] items-center gap-2 border-b border-[var(--border-color)] px-4 py-2">
          <Undo2 size={14} className="text-accent" />
          <span className="text-sm font-extrabold">Return details</span>
          <span className="text-[11px] text-[var(--text-muted)]">optional</span>
        </div>
        <div data-compact-form="true" className="grid grid-cols-1 gap-1.5 px-3 py-2.5 sm:grid-cols-2 sm:gap-x-5 sm:gap-y-2 sm:px-4 sm:py-3">
          <FilterField label="Customer" className="sm:[&>span]:w-[118px]">
            <SearchableSelect
              options={customerOptions}
              value={customerId === '' ? '' : Number(customerId)}
              onChange={(v) => setCustomerId(v === '' ? '' : Number(v))}
              placeholder="Search customer…"
            />
          </FilterField>
          <FilterField label="Invoice No" className="sm:[&>span]:w-[74px]">
            <div className="relative">
              <Input
                value={invoiceNo}
                onChange={(e) => { setInvoiceNo(e.target.value); setOrderQuery(''); setInvoiceOpen(true); }}
                onFocus={() => setInvoiceOpen(true)}
                onBlur={() => setTimeout(() => setInvoiceOpen(false), 150)}
                placeholder={customerId !== '' ? 'Select an invoice or type any' : 'Type / pick — customer auto-fills'}
                autoComplete="off"
              />
              {invoiceOpen && invoiceMatches.length > 0 && (
                <ul className="absolute z-30 mt-1 max-h-56 w-full overflow-auto rounded-lg border border-[var(--border-color)] bg-[var(--card-bg)] shadow-lg backdrop-blur-xl">
                  {invoiceMatches.map((inv) => (
                    <li key={inv.invoiceNo}>
                      <button
                        type="button"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => { setInvoiceNo(inv.invoiceNo); setOrderQuery(''); setInvoiceOpen(false); }}
                        className={`flex w-full items-center justify-between gap-2 px-3 py-1.5 text-left text-[12px] hover:bg-accent/10 ${
                          invoiceNo === inv.invoiceNo ? 'bg-accent/[0.06]' : ''
                        }`}
                      >
                        <span className="font-semibold text-[var(--text-primary)]">{inv.invoiceNo}</span>
                        <span className="shrink-0 text-[10px] text-[var(--text-muted)]">{formatDate(inv.invoiceDate)} · ₹{inv.grandTotal.toLocaleString('en-IN')}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </FilterField>
          <FilterField label="Online Order / AWB" className="sm:[&>span]:w-[118px]">
            <Input
              value={orderQuery}
              onChange={(e) => { setOrderQuery(e.target.value); if (e.target.value.trim()) setInvoiceNo(''); }}
              placeholder="e.g. #20470 or AWB"
              autoComplete="off"
            />
          </FilterField>
          <FilterField label="Remarks" className="sm:[&>span]:w-[74px]">
            <Input value={remarks} onChange={(e) => setRemarks(e.target.value)} placeholder="Reason / reference" />
          </FilterField>
        </div>
        {(onlineError || online?.alreadyReturnedNo) && (
          <div className="border-t border-[var(--border-color)] px-3 py-2 text-[12px] font-semibold text-danger sm:px-4">
            {online?.alreadyReturnedNo
              ? `This parcel was already received back — return ${online.alreadyReturnedNo}.`
              : onlineError}
          </div>
        )}
      </div>

      {/* Expected to return — the articles on the selected invoice, with live
          scanned/expected progress so the operator knows what should come back. */}
      {expected && expected.items.length > 0 && (
        <div className="shrink-0 rounded-card border border-info/30 bg-info/[0.05] p-3 sm:p-4">
          <div className="mb-2 flex items-center justify-between gap-2">
            <div className="text-[11px] font-bold uppercase tracking-wide text-[var(--text-muted)]">
              Expected to Return · {expected.title ?? `Invoice ${expected.invoiceNo}`}
            </div>
            <div className="flex items-center gap-3">
              {qtyLinePieces > 0 && <span className="text-[10px] font-semibold text-accent">{qtyLinePieces} pcs to return</span>}
              <span className="text-[10px] text-[var(--text-muted)]">{expected.items.length} article(s)</span>
              {online ? (
                <span className="text-[10px] font-semibold text-accent">Scan each piece's label to receive it</span>
              ) : (
                <label className="flex cursor-pointer items-center gap-1.5 text-[11px] font-semibold text-[var(--text-secondary)]">
                  <input type="checkbox" checked={allExpectedSelected} onChange={toggleAllExpected} className="accent-accent" />
                  Select all
                </label>
              )}
            </div>
          </div>
          <div className="grid gap-1.5 sm:grid-cols-2 lg:grid-cols-3">
            {expected.items.map((it) => {
              const key = `${it.itemCode}|${it.itemName}`;
              const isSel = selected.has(key);
              const scanned = scannedByName.get(it.itemName.toLowerCase()) ?? 0;
              const done = scanned >= it.qty;
              return (
                <div
                  key={key}
                  className={`flex items-center gap-2 rounded-md border px-2.5 py-1.5 ${
                    isSel ? 'border-accent bg-accent/[0.06]' : 'border-[var(--border-color)] bg-[var(--card-bg)]'
                  }`}
                >
                  {!online && <input type="checkbox" checked={isSel} onChange={() => toggleExpected(key)} className="accent-accent shrink-0 cursor-pointer" />}
                  <ItemThumb src={it.imageUrl} size={28} />
                  <div className={`min-w-0 flex-1 ${online ? '' : 'cursor-pointer'}`} onClick={() => { if (!online) toggleExpected(key); }}>
                    <div className="truncate text-[12px] font-semibold text-[var(--text-primary)]">{it.itemName}</div>
                    {(it.itemCode || it.barcode) && (
                      <div className="truncate text-[10px] text-[var(--text-muted)]">{[it.itemCode, it.barcode].filter(Boolean).join(' · ')}</div>
                    )}
                  </div>
                  {isSel && it.itemCode ? (
                    <div className="flex shrink-0 items-center gap-1" title="Quantity to return (no scan needed)">
                      <input
                        type="number"
                        min={1}
                        max={it.qty}
                        value={qtyByKey[key] ?? it.qty}
                        onChange={(e) => setQtyForKey(key, e.target.value)}
                        className="w-14 rounded border border-[var(--border-color)] bg-[var(--input-bg)] px-1.5 py-0.5 text-center text-[12px] font-semibold text-[var(--text-primary)]"
                      />
                      <span className="text-[10px] text-[var(--text-muted)]">/ {it.qty}</span>
                      <button
                        type="button"
                        onClick={() => setConditionByKey((prev) => ({ ...prev, [key]: (prev[key] ?? 'Good') === 'Good' ? 'Damaged' : 'Good' }))}
                        title="Tap to switch Good / Damaged"
                        className={`ml-1 rounded px-1.5 py-0.5 text-[10px] font-bold ${
                          (conditionByKey[key] ?? 'Good') === 'Good' ? 'bg-success/15 text-success' : 'bg-danger/15 text-danger'
                        }`}
                      >
                        {conditionByKey[key] ?? 'Good'}
                      </button>
                    </div>
                  ) : (
                    <span className={`shrink-0 rounded px-1.5 py-0.5 text-[11px] font-bold ${done ? 'bg-success/15 text-success' : 'bg-[var(--bg-soft)] text-[var(--text-secondary)]'}`}>
                      {scanned}/{it.qty}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Working area — scan on the left, returned items on the right; fills the
          remaining viewport height so the page itself doesn't scroll. */}
      <div className="grid gap-3 lg:min-h-0 lg:flex-1 lg:grid-cols-12">
        {/* Scan returns — left. Online parcels too: pick a location, then scan each piece's label. */}
        {(
        <div className="rounded-card border border-[var(--border-color)] bg-[var(--card-bg)] backdrop-blur-xl lg:col-span-5 lg:self-start">
          <div className="flex min-h-[44px] items-center gap-2 border-b border-[var(--border-color)] px-4 py-2">
            <ScanLine size={14} className="text-accent" />
            <span className="text-sm font-extrabold">Scan returns</span>
          </div>
          {/* Phone: step label on its own line, the field full width under it (sm:contents keeps the desktop row). */}
          <div data-compact-form="true" className="flex flex-col gap-3 px-3 py-2.5 sm:px-4 sm:py-3">
            <div>
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1 sm:flex-nowrap">
                <StepBadge n={1} done={canScan} />
                <span className={STEP_LABEL}>Location</span>
                <div className="w-full min-w-0 sm:w-auto sm:flex-1">
                  <SearchableSelect
                    options={locationOptions}
                    value={locationCode}
                    onChange={(v) => setLocationCode(v === '' ? '' : String(v))}
                    placeholder="Search / scan location…"
                    leftIcon={<ScanLine size={13} />}
                    className={canScan ? undefined : 'rounded-lg ring-1 ring-accent'}
                  />
                </div>
              </div>
              <div className={STEP_HINT}>{canScan ? `Placing into ${locationCode}` : 'Required before scanning'}</div>
            </div>

            <div>
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1 sm:flex-nowrap">
                <StepBadge n={2} done={false} muted={!canScan} />
                <span className={STEP_LABEL}>Barcode</span>
                <div className="flex w-full min-w-0 items-center gap-2 sm:contents">
                <div className={`relative min-w-0 flex-1 rounded-lg ${canScan ? 'ring-2 ring-accent ring-offset-2 ring-offset-[var(--card-bg)]' : ''}`}>
                  <ScanLine size={13} className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
                  <Input
                    ref={inputRef}
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') void handleScan(); }}
                    disabled={scanning || !canScan}
                    placeholder={canScan ? 'Scan / type — Enter' : 'Set a location first'}
                    className="!pl-8"
                    autoComplete="off"
                  />
                </div>
                <Button variant="glass" size="sm" className="shrink-0 justify-center sm:w-20" onClick={() => void handleScan()} disabled={scanning || !input.trim() || !canScan}>
                  {scanning ? 'Checking…' : 'Add'}
                </Button>
                </div>
              </div>
              <div className={STEP_HINT}>{online ? "Scan each piece's label (or its barcode) — one piece per scan" : 'Reuses the original serial — a whole box in one scan'}</div>
            </div>
          </div>
        </div>
        )}

        {/* Returned items — right */}
        <div className="flex min-h-[300px] flex-col rounded-card border border-[var(--border-color)] bg-[var(--card-bg)] backdrop-blur-xl lg:col-span-7 lg:min-h-0">
          <div className="flex min-h-[44px] items-center justify-between border-b border-[var(--border-color)] px-4 py-2">
            <span className="flex items-center gap-2 text-sm font-extrabold">
              <Boxes size={14} className="text-accent" />
              Returned items <span className="font-semibold text-[var(--text-muted)]">({lines.length})</span>
            </span>
            <Button
              variant="primary"
              size="sm"
              onClick={() => saveMut.mutate()}
              disabled={saveMut.isPending || (lines.length === 0 && qtyLines.length === 0) || !!online?.alreadyReturnedNo}
            >
              {saveMut.isPending ? 'Saving…' : 'Save Return'}
            </Button>
          </div>
          {lines.length === 0 ? (
            <div className="flex flex-1 flex-col items-center justify-center gap-2 px-4 py-10 text-center">
              <ScanLine size={30} className="text-[var(--text-muted)] opacity-40" />
              <div className="text-[12px] text-[var(--text-muted)]">
                {canScan ? 'Scan a returned barcode to add it here.' : 'Pick a location, then scan returns.'}
              </div>
              {qtyLines.length > 0 ? (
                <div className="text-[11px] font-semibold text-accent">
                  {qtyLines.length} article(s) · {qtyLinePieces} pcs set by quantity above — ready to save.
                </div>
              ) : !online ? (
                <div className="text-[11px] text-[var(--text-muted)]">…or set quantities in “Expected to Return” above.</div>
              ) : null}
            </div>
          ) : (
            <div className="flex-1 divide-y divide-[var(--border-color)] overflow-y-auto">
              {lines.map((l, i) => (
                <div key={l.serialNo} className="flex items-center gap-3 px-3 py-2 sm:px-5">
                  <ItemThumb src={l.imageUrl} />
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-[12px] font-bold text-[var(--text-primary)]">
                      {l.serialNo}{l.maxQty != null
                        ? <span className="ml-1.5 rounded bg-accent/15 px-1.5 py-0.5 text-[10px] font-bold text-accent">{l.qty} of {l.maxQty} pc{l.maxQty === 1 ? '' : 's'}</span>
                        : l.qty > 1 ? <span className="ml-1.5 rounded bg-accent/15 px-1.5 py-0.5 text-[10px] font-bold text-accent">Box ×{l.qty}</span> : null}
                    </div>
                    <div className="truncate text-[11px] text-[var(--text-muted)]">{l.itemName}{l.barcode ? ` · ${l.barcode}` : ''}</div>
                  </div>
                  <div className="w-32 shrink-0">
                    <Select
                      value={l.condition}
                      onChange={(e) =>
                        setLines((prev) => prev.map((x, xi) => (xi === i ? { ...x, condition: e.target.value as ReturnLine['condition'] } : x)))
                      }
                    >
                      <option value="Good">Good</option>
                      <option value="Damaged">Damaged</option>
                    </Select>
                  </div>
                  <button
                    type="button"
                    onClick={() => setLines((prev) => prev.filter((_, xi) => xi !== i))}
                    className="shrink-0 rounded p-1.5 text-danger hover:bg-danger/10"
                    title="Remove"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="shrink-0 rounded-md border border-info/30 bg-info/[0.05] px-3 py-1.5 text-xs text-[var(--text-secondary)]">
        <Lightbulb size={13} className="mr-1 inline-block align-[-2px]" /> <strong>Good</strong> returns go straight back to sellable stock; <strong>Damaged</strong> ones are quarantined (not sellable).
      </div>
      </>
      )}

      {/* Return history — every recorded return with its tracking details. */}
      {tab === 'history' && (
      <>
      <FilterBar>
        <FilterField label="Sort By" className="sm:min-w-[150px]">
          <Select value={sortBy} onChange={(e) => setSortBy(e.target.value as 'date' | 'customer' | 'invoice')}>
            <option value="date">Date</option>
            <option value="customer">Customer</option>
            <option value="invoice">Invoice No</option>
          </Select>
        </FilterField>
        <FilterField label="Order" className="sm:min-w-[140px]">
          <Select value={sortDir} onChange={(e) => setSortDir(e.target.value as 'asc' | 'desc')}>
            <option value="desc">Descending</option>
            <option value="asc">Ascending</option>
          </Select>
        </FilterField>
        <div className="col-span-2 sm:col-span-1 sm:ml-auto sm:self-end">
          <Button
            variant="glass"
            size="sm"
            icon={<Download size={12} />}
            disabled={returns.length === 0}
            onClick={() => downloadExcel(salesReturnsExportPath({ sortBy, dir: sortDir }), 'GRHAMOY_Sales_Returns.xlsx', 120000)}
            className="w-full sm:w-auto"
            title="Export the full return history (no cap) in the current sort order"
          >
            Export
          </Button>
        </div>
      </FilterBar>
      <Table<SalesReturnSummary>
        storageKey="sales-return-history"
        paginated
        title="Return History"
        count={`${returns.length} return${returns.length === 1 ? '' : 's'}`}
        rows={sortedReturns}
        rowKey={(r) => r.id}
        loading={returnsQ.isFetching && returns.length === 0}
        empty="No returns recorded yet."
        columns={[
          { key: 'no', header: 'Return No', minWidth: '130px', cell: (r) => <span className="font-extrabold text-accent">{r.returnNo}</span> },
          { key: 'date', header: 'Date', minWidth: '100px', cell: (r) => formatDate(r.returnDate) },
          { key: 'customer', header: 'Customer', minWidth: '160px', cell: (r) => <span className="font-bold">{r.partyName || '—'}</span> },
          { key: 'invoice', header: 'Invoice / Order', minWidth: '130px', cell: (r) => r.invoiceNo || (r.orderRef ? `Order ${r.orderRef}` : '—') },
          { key: 'items', header: 'Items', align: 'right', cell: (r) => r.itemCount },
          { key: 'good', header: 'Good', align: 'right', cell: (r) => (r.goodQty ? <span className="text-success font-semibold">{r.goodQty}</span> : '—') },
          { key: 'damaged', header: 'Damaged', align: 'right', cell: (r) => (r.damagedQty ? <span className="text-danger font-semibold">{r.damagedQty}</span> : '—') },
          { key: 'total', header: 'Total Qty', align: 'right', cell: (r) => <strong>{r.totalQty}</strong> },
          { key: 'by', header: 'By', minWidth: '120px', cell: (r) => r.createdBy || '—' },
          { key: 'remarks', header: 'Remarks', minWidth: '140px', cell: (r) => r.remarks || '—' },
          {
            key: 'view', header: '', align: 'right', alwaysVisible: true,
            cell: (r) => (
              <button type="button" onClick={() => setDetail(r)} className="inline-flex items-center gap-1 rounded p-1.5 text-accent hover:bg-accent/10" title="View returned items">
                <Eye size={14} />
              </button>
            ),
          },
        ]}
      />
      </>
      )}

      {detail && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={() => setDetail(null)}>
          <div className="w-full max-w-2xl rounded-xl border border-[var(--border-color)] shadow-2xl" style={{ background: 'var(--bg-primary)' }} onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-[var(--border-color)] px-5 py-4">
              <div>
                <h2 className="text-base font-bold text-[var(--text-primary)]">{detail.returnNo}</h2>
                <div className="text-[11px] text-[var(--text-muted)]">
                  {formatDate(detail.returnDate)}{detail.partyName ? ` · ${detail.partyName}` : ''}{detail.invoiceNo ? ` · Invoice ${detail.invoiceNo}` : ''}{detail.orderRef ? ` · Order ${detail.orderRef}` : ''}{detail.awbNo ? ` · AWB ${detail.awbNo}` : ''}
                </div>
              </div>
              <button onClick={() => setDetail(null)} className="text-[var(--text-muted)] hover:text-[var(--text-primary)]"><X size={18} /></button>
            </div>
            <div className="max-h-[60vh] overflow-auto p-5">
              <div className="overflow-hidden rounded-card border border-[var(--border-color)]">
                <table className="w-full border-collapse text-left text-[12px]">
                  <thead>
                    <tr>
                      <th className="border-b-2 border-r border-[var(--border-color)] bg-orange-50 px-3 py-2 text-[11px] font-black uppercase tracking-wide text-accent">Serial</th>
                      <th className="border-b-2 border-r border-[var(--border-color)] bg-orange-50 px-3 py-2 text-[11px] font-black uppercase tracking-wide text-accent">Item</th>
                      <th className="border-b-2 border-r border-[var(--border-color)] bg-orange-50 px-3 py-2 text-right text-[11px] font-black uppercase tracking-wide text-accent">Qty</th>
                      <th className="border-b-2 border-[var(--border-color)] bg-orange-50 px-3 py-2 text-[11px] font-black uppercase tracking-wide text-accent">Condition</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detail.items.map((it, i) => (
                      <tr key={`${it.serialNo}-${i}`} className="border-b border-[var(--border-color)] last:border-b-0 odd:bg-[var(--bg-secondary)]/60">
                        <td className="border-r border-[var(--border-color)] px-3 py-1.5 font-mono font-semibold text-[var(--text-primary)]">{it.serialNo}</td>
                        <td className="border-r border-[var(--border-color)] px-3 py-1.5 text-[var(--text-primary)]">{it.itemName}</td>
                        <td className="border-r border-[var(--border-color)] px-3 py-1.5 text-right font-semibold text-[var(--text-primary)]">{it.qty}</td>
                        <td className="px-3 py-1.5">
                          <span className={`rounded px-1.5 py-0.5 text-[10px] font-bold ${it.condition === 'Damaged' ? 'bg-danger/15 text-danger' : 'bg-success/15 text-success'}`}>{it.condition}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {detail.remarks && <div className="mt-3 text-[11px] text-[var(--text-secondary)]"><strong>Remarks:</strong> {detail.remarks}</div>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function TabButton({ active, onClick, label }: { active: boolean; onClick: () => void; label: string }) {
  return (
    <button
      onClick={onClick}
      className={
        'rounded-md px-3 py-1.5 text-[11px] font-bold transition sm:text-xs ' +
        (active
          ? 'bg-accent text-white shadow-sm'
          : 'text-[var(--text-muted)] hover:bg-[var(--bg-soft)] hover:text-[var(--text-primary)]')
      }
    >
      {label}
    </button>
  );
}

const STEP_LABEL = 'w-16 shrink-0 whitespace-nowrap text-[10px] font-bold uppercase tracking-wide text-[var(--text-muted)]';
// Indented to sit under the field: badge (15px) + label (64px) + two 8px gaps.
const STEP_HINT = 'mt-1 text-[11px] text-[var(--text-muted)] sm:pl-[95px]';

function StepBadge({ n, done, muted }: { n: number; done: boolean; muted?: boolean }) {
  return (
    <span
      className={
        'inline-flex h-[15px] w-[15px] items-center justify-center rounded-full text-[9px] font-extrabold ' +
        (done ? 'bg-success text-white' : muted ? 'bg-[var(--bg-soft)] text-[var(--text-muted)]' : 'bg-accent text-white')
      }
    >
      {done ? <Check size={14} /> : n}
    </span>
  );
}
